import {
  Box,
  Card,
  CardContent,
  Link,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import { visuallyHidden } from '@mui/utils';
import { ReferenceField, TextField, useTranslate } from 'react-admin';
import FullNameField from './FullNameField';

const useStyles = makeStyles({
  title: {
    fontSize: '26px',
    lineHeight: '31px',
  },
  row: {
    padding: '4px 0',
    '& .MuiTableCell-root': {
      borderBottom: 'none',
    },
  },
  link: {
    display: 'inline-block',
    maxWidth: '500px',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
});

const TableRowsLoader = ({ rowsNum }) => {
  return [...Array(rowsNum)].map((row, index) => (
    <TableRow key={index}>
      <TableCell width="44px">
        <Skeleton height="26px" animation="wave" variant="text" />
      </TableCell>
      <TableCell>
        <Skeleton height="26px" animation="wave" variant="text" />
      </TableCell>
      <TableCell>
        <Skeleton height="26px" animation="wave" variant="text" />
      </TableCell>
      <TableCell>
        <Skeleton height="26px" animation="wave" variant="text" />
      </TableCell>
      <TableCell>
        <Skeleton height="26px" animation="wave" variant="text" />
      </TableCell>
      <TableCell>
        <Skeleton height="26px" animation="wave" variant="text" />
      </TableCell>
      <TableCell>
        <Skeleton height="26px" animation="wave" variant="text" />
      </TableCell>
      <TableCell>
        <Skeleton height="26px" animation="wave" variant="text" />
      </TableCell>
    </TableRow>
  ));
};

export const NotesTable = ({
  data,
  title,
  actions,
  order,
  orderBy,
  setOrderBy,
  loading,
}) => {
  const classes = useStyles();
  const t = useTranslate();
  const handleRequestSort = property => {
    if (!loading) {
      setOrderBy(property);
    }
  };

  return (
    <Card>
      <CardContent>
        <Box
          mb="12px"
          display="flex"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography variant="h6" component="h2" className={classes.title}>
            {title}
          </Typography>
          <Box display="flex" gap="16px" alignItems="center">
            {actions}
          </Box>
        </Box>
        <TableContainer component={Paper}>
          <Table sx={{ minWidth: 650 }} aria-label="notes table" size="medium">
            <TableHead>
              <TableRow>
                <TableCell>{t('resources.reports.fields.top')}</TableCell>
                <TableCell align="left">
                  {t('resources.reports.fields.notes')}
                </TableCell>
                <TableCell align="left">
                  {t('resources.reports.fields.section')}
                </TableCell>
                <TableCell align="left">
                  {t('resources.reports.fields.team')}
                </TableCell>
                <TableCell align="left">
                  {t('resources.reports.fields.author')}
                </TableCell>
                <TableCell
                  align="left"
                  sortDirection={orderBy === 'total_visits' ? order : false}
                >
                  <TableSortLabel
                    active={orderBy === 'total_visits'}
                    direction={orderBy === 'total_visits' ? order : 'desc'}
                    onClick={() => handleRequestSort('total_visits')}
                  >
                    {t('resources.reports.fields.visits')}
                    {orderBy === 'total_visits' ? (
                      <Box component="span" sx={visuallyHidden}>
                        {order === 'desc'
                          ? 'sorted descending'
                          : 'sorted ascending'}
                      </Box>
                    ) : null}
                  </TableSortLabel>
                </TableCell>
                <TableCell
                  align="left"
                  sortDirection={orderBy === 'total_rfv' ? order : false}
                >
                  <TableSortLabel
                    active={orderBy === 'total_rfv'}
                    direction={orderBy === 'total_rfv' ? order : 'desc'}
                    onClick={() => handleRequestSort('total_rfv')}
                  >
                    {t('resources.reports.fields.rfv')}
                    {orderBy === 'total_rfv' ? (
                      <Box component="span" sx={visuallyHidden}>
                        {order === 'desc'
                          ? 'sorted descending'
                          : 'sorted ascending'}
                      </Box>
                    ) : null}
                  </TableSortLabel>
                </TableCell>
                <TableCell
                  align="left"
                  sortDirection={
                    orderBy === 'total_subscribers' ? order : false
                  }
                >
                  <TableSortLabel
                    active={orderBy === 'total_subscribers'}
                    direction={orderBy === 'total_subscribers' ? order : 'desc'}
                    onClick={() => handleRequestSort('total_subscribers')}
                  >
                    {t('resources.reports.fields.subscribers')}
                    {orderBy === 'total_subscribers' ? (
                      <Box component="span" sx={visuallyHidden}>
                        {order === 'desc'
                          ? 'sorted descending'
                          : 'sorted ascending'}
                      </Box>
                    ) : null}
                  </TableSortLabel>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data && !loading ? (
                data?.map((row, index) => (
                  <TableRow key={index} className={classes.row} size="medium">
                    <TableCell size="medium">
                      <Typography
                        color="primary"
                        textAlign="center"
                        fontWeight="600"
                      >
                        {order === 'desc' ? index + 1 : data?.length - index}
                      </Typography>
                    </TableCell>
                    <TableCell align="left" size="medium">
                      <Link
                        href={
                          process.env.REACT_APP_URL_CLIENT + `${row?.path_note}`
                        }
                        target="_blank"
                        color="#000"
                        className={classes.link}
                        rel="noopener noreferrer"
                      >
                        {row?.title_note}
                      </Link>
                    </TableCell>
                    <TableCell align="left" size="medium">
                      <Typography>{row.section}</Typography>
                    </TableCell>
                    <TableCell align="left" size="medium">
                      <ReferenceField
                        record={row}
                        reference="teams"
                        source="team_id"
                      >
                        <TextField source="name" />
                      </ReferenceField>
                    </TableCell>
                    <TableCell align="left" size="large">
                      <Box display="flex" gap="4px" alignItems="center">
                        <ReferenceField
                          record={row}
                          reference="members"
                          source="user_id"
                        >
                          <FullNameField
                            source="username"
                            label="Nombre y apellido"
                          />
                        </ReferenceField>
                      </Box>
                    </TableCell>
                    <TableCell align="left" size="medium">
                      {Intl.NumberFormat('es-AR', {
                        maximumFractionDigits: 2,
                      }).format(row?.total_visits)}
                    </TableCell>
                    <TableCell align="left" size="medium">
                      {Intl.NumberFormat('es-AR', {
                        maximumFractionDigits: 2,
                      }).format(row?.total_rfv)}
                    </TableCell>
                    <TableCell align="left" size="medium">
                      {Intl.NumberFormat('es-AR', {
                        maximumFractionDigits: 2,
                      }).format(row?.total_subscribers)}
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRowsLoader rowsNum={10} />
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </CardContent>
    </Card>
  );
};
